import { useQuery, type QueryKey } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { getMetricsBatch } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName } from '@/features/shared/tags';
import { useSuiteOptions } from '@/features/suites';
import { DeleteTestDialog } from '@/features/tests/components/dialogs/TestDialogs';
import { testsGridConfig } from '@/features/tests/grid/gridConfig';
import { useTagOptions, queryKeys } from '@/shared/hooks';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type DataGridProps, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildTestsColumns } from './testsColumns';
import { useTestsTableRowActions } from './useTestsTableRowActions';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';

type TestsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: TestListItemDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: TestListItemDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showSuiteLinks?: boolean;
  includeSuiteInNameColumn?: boolean;
  onStatusSelect?: (status: TestExecutionStatus | null) => void;
  showDeltas?: boolean;
  sparklineRangeDays?: number;
  tokenOverride?: string | null;
  disableLinks?: boolean;
  search?: string;
  metricsStartDay?: string;
  metricsEndDay?: string;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
};

const TestsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No tests found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteLinks = true,
  includeSuiteInNameColumn = true,
  onStatusSelect,
  showDeltas = true,
  sparklineRangeDays = 7,
  tokenOverride,
  disableLinks = false,
  search,
  metricsStartDay,
  metricsEndDay,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: TestsTableProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const { suiteOptions } = useSuiteOptions({ projectId: selectedProjectId });
  const [visibleRows, setVisibleRows] = useState<TestListItemDto[]>([]);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const {
    deleteTarget,
    deleteLoading,
    deleteRowPendingId,
    updateRowTagsColorPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
  } = useTestsTableRowActions({
    resolvedToken,
    selectedProjectId,
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.tests]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const metricsEntityKey = useMemo(
    () => visibleRows.map((row) => `${row.projectId}:${row.id}`).join(','),
    [visibleRows],
  );
  const metricsBatchQuery = useQuery({
    queryKey: queryKeys.metricsBatch(
      selectedOrganisationId ?? null,
      'test',
      metricsRange,
      metricsEntityKey,
      metricsRangeKey,
    ),
    enabled: resolvedToken !== null && visibleRows.length > 0,
    staleTime: 30_000,
    queryFn: async () =>
      await getMetricsBatch(
        {
          range: metricsRange,
          scope: 'test',
          startDay: metricsStartDay ?? null,
          endDay: metricsEndDay ?? null,
          entities: visibleRows.map((row) => ({ entityId: row.id, projectId: row.projectId })),
        },
        selectedOrganisationId ?? undefined,
        resolvedToken,
      ),
  });
  const metricSeriesByTestId = useMemo(() => {
    const metricsByTestId = new Map(
      (metricsBatchQuery.data ?? []).map((item) => [item.entityId, item.metrics]),
    );
    const result = new Map<
      string,
      {
        dates: Date[];
        passRate: number[];
        frequencyPerDay: number[];
        averageDurationMs: number[];
      }
    >();
    visibleRows.forEach((row) => {
      const queryData = metricsByTestId.get(row.id);
      if (!queryData) return;
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = queryData.series.filteredPassRate.map((point) => point.value);
      const frequencyPerDay = queryData.series.executionTrendMetrics.map((point) => point.value);
      const averageDurationMs = queryData.series.executionDurationAverage.map(
        (point) => point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, averageDurationMs });
    });
    return result;
  }, [metricsBatchQuery.data, visibleRows]);
  const shouldQueryPerRowMetrics = resolvedToken !== null;
  const metricsQueriesReady =
    !shouldQueryPerRowMetrics ||
    visibleRows.length === 0 ||
    metricsBatchQuery.data !== undefined ||
    (!metricsBatchQuery.isPending && !metricsBatchQuery.isFetching);
  const filterSuites = suiteOptions;
  const filterTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const defaultColumns = useMemo(
    () =>
      buildTestsColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.tests,
        resolveThresholds,
        showSuiteLinks: showSuiteLinks && !disableLinks,
        combineNameAndSuite: true,
        includeSuiteInNameColumn,
        includeTagsInNameColumn: true,
        includeActionsColumn: true,
        disableColumnResizeHandles: false,
        latestAsMeta: true,
        includeLastExecutedColumn: false,
        disableLinks,
        showDeltas,
        metricSeriesByTestId,
        filterSuites,
        filterTags,
        onDeleteRow,
        deleteRowPendingId,
        tagColorByName,
        onUpdateRowTagsColor,
        updateRowTagsColorPendingId,
      }),
    [
      disableLinks,
      filterSuites,
      filterTags,
      includeSuiteInNameColumn,
      metricSeriesByTestId,
      onDeleteRow,
      onStatusSelect,
      onUpdateRowTagsColor,
      resolveThresholds,
      setSelectedProjectId,
      showDeltas,
      showSuiteLinks,
      tagColorByName,
      thresholds.tests,
      updateRowTagsColorPendingId,
      deleteRowPendingId,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set([
      'suiteId',
      'tags',
      'statusFilter',
      'latestExecutionDurationMs',
      'latestExecutionTimestamp',
    ]);
    return defaultColumns
      .map((column) => column.field)
      .filter((field) => !helperOnlyFields.has(field));
  }, [defaultColumns]);

  const gridConfig = testsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'tests-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
    include: showSuiteLinks
      ? undefined
      : {
          latestExecution: true,
          executionStatusCounts: true,
          metrics: true,
          metricDeltas: showDeltas,
        },
  });

  return (
    <>
      <ServerDataTable<TestListItemDto>
        queryPath="tests/query"
        {...gridConfig}
        queryFn={queryFn ?? gridConfig.queryFn}
        queryKey={queryKey ?? gridConfig.queryKey}
        columns={defaultColumns}
        getRowId={(row) => row.id}
        token={resolvedToken}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        disableCellFocus
        getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        showSkeletonOnFetch
        showLastUpdated={showLastUpdated}
        autoHeight
        externalLoading={!metricsQueriesReady}
        onRowsChange={(rows) => {
          setVisibleRows(rows);
          onRowsChange?.(rows);
        }}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={tableSx}
      />
      <DeleteTestDialog
        open={deleteTarget !== null}
        testName={deleteTarget?.name}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const TestsTable = memo(TestsTableComponent);
